"use client";

import { faArrowLeft } from "@fortawesome/pro-solid-svg-icons";
import { Button, SquareButton, cn } from "@tosize/ui";
import { Fragment, useState } from "react";
import { StyledLink } from "~/components/Link";
import type { Category } from "~/graphql/genql/magento";
import type { getWebsite } from "~/graphql/shared/website";

type CurrentWebsite = Awaited<ReturnType<typeof getWebsite>>["current"];
type RootCategory = CurrentWebsite["rootCategory"];

interface MenuLayerProps {
	items: RootCategory["children"];
}

export function MenuLayer({ items }: MenuLayerProps) {
	const [activeCategory, setActiveCategory] = useState<Category["id"]>();

	return (
		<Fragment>
			{items.map((child) => (
				<Fragment key={child.id}>
					{child.children.length > 0 ? (
						<Fragment>
							<Button
								onClick={() => {
									setActiveCategory(child.id);
								}}
								variant="link"
								className="font-black text-2xl"
							>
								{child.name}
							</Button>

							<MenuLayerItem
								back={() => {
									setActiveCategory(undefined);
								}}
								category={child}
								open={activeCategory === child.id}
							/>
						</Fragment>
					) : (
						<StyledLink href={child.url} variant="link" className="font-black text-2xl">
							{child.name}
						</StyledLink>
					)}
				</Fragment>
			))}
		</Fragment>
	);
}

interface MenuLayerItemProps {
	category: RootCategory["children"][number];
	open: boolean;
	back: () => void;
}

function MenuLayerItem({ category, open, back }: MenuLayerItemProps) {
	return (
		<div
			className={cn(
				"fixed top-[4rem] left-4 mt-4 flex h-full w-full xs:max-w-[calc(32rem-2rem)] flex-col bg-shades-light motion-safe:transition-all sm:top-[5.5rem] sm:left-8 sm:max-w-[calc(32rem-3rem)] md:pl-8",
				{ flex: open, hidden: !open },
			)}
		>
			<SquareButton
				title="close"
				component="button"
				icon={faArrowLeft}
				color="shades"
				size="small"
				className="md:-left-2 absolute top-0 left-0 my-1"
				onClick={() => back()}
			/>

			<div className="flex flex-col space-y-4">
				<StyledLink
					href={category.url}
					color="dark"
					className="py-1 pl-10 font-extrabold text-2xl md:pl-0"
				>
					{category.name}
				</StyledLink>

				<div className="flex flex-col items-start space-y-3">
					{category.children.map((child) => (
						<StyledLink key={child.id} href={child.url} className="font-black text-xl">
							{child.name}
						</StyledLink>
					))}
				</div>
			</div>
		</div>
	);
}
